/**
 * Paddle 性能监控工具
 * 监控 Paddle 加载和初始化性能
 */

import { log } from './logger';
import { analytics } from '../services/analyticsService';

interface PerformanceMetrics {
  startTime: number;
  endTime?: number;
  duration?: number;
  success: boolean;
  error?: string;
}

class PaddlePerformanceMonitor {
  private metrics: Map<string, PerformanceMetrics> = new Map();

  /**
   * 开始监控
   */
  startMonitoring(operation: string): void {
    this.metrics.set(operation, {
      startTime: performance.now(),
      success: false,
    });
    
    log.debug(`开始监控 Paddle 操作: ${operation}`);
  }

  /**
   * 结束监控（成功）
   */
  endMonitoring(operation: string): void {
    const metric = this.metrics.get(operation);
    if (!metric) return;

    const endTime = performance.now();
    const duration = endTime - metric.startTime;

    metric.endTime = endTime;
    metric.duration = duration;
    metric.success = true;

    log.info(`Paddle 操作完成: ${operation}, 耗时: ${duration.toFixed(2)}ms`);

    // 发送性能数据到 GA
    this.sendPerformanceToAnalytics(operation, duration, true);
  }

  /**
   * 结束监控（失败）
   */
  endMonitoringWithError(operation: string, error: string): void {
    const metric = this.metrics.get(operation);
    if (!metric) return;

    const endTime = performance.now();
    const duration = endTime - metric.startTime;

    metric.endTime = endTime;
    metric.duration = duration;
    metric.success = false;
    metric.error = error;

    log.warn(`Paddle 操作失败: ${operation}, 耗时: ${duration.toFixed(2)}ms, 错误: ${error}`);

    // 发送性能数据到 GA
    this.sendPerformanceToAnalytics(operation, duration, false, error);
  }

  /**
   * 发送性能数据到 Google Analytics
   */
  private sendPerformanceToAnalytics(operation: string, duration: number, success: boolean, error?: string): void {
    try {
      // 发送自定义事件
      analytics.trackEvent('paddle_performance', {
        event_category: 'performance',
        event_label: operation,
        value: Math.round(duration),
        success: success,
        error_message: error,
      });

      // 发送性能计时事件
      analytics.trackEvent('timing_complete', {
        name: `paddle_${operation}`,
        value: Math.round(duration),
        event_category: 'paddle_timing',
      });
    } catch (analyticsError) {
      log.warn('发送 Paddle 性能数据到 GA 失败:', analyticsError);
    }
  }

  /**
   * 获取性能报告
   */
  getPerformanceReport(): Record<string, PerformanceMetrics> {
    const report: Record<string, PerformanceMetrics> = {};
    
    this.metrics.forEach((metric, operation) => {
      report[operation] = { ...metric };
    });

    return report;
  }

  /**
   * 清除性能数据
   */
  clearMetrics(): void {
    this.metrics.clear();
  }

  /**
   * 获取平均性能
   */
  getAveragePerformance(operation: string): number | null {
    const allMetrics = Array.from(this.metrics.entries())
      .filter(([key, metric]) => key.includes(operation) && metric.success && metric.duration)
      .map(([, metric]) => metric.duration!);

    if (allMetrics.length === 0) return null;

    return allMetrics.reduce((sum, duration) => sum + duration, 0) / allMetrics.length;
  }
}

// 创建全局实例
export const paddlePerformanceMonitor = new PaddlePerformanceMonitor();

// 导出便捷方法
export const startPaddleMonitoring = (operation: string) => paddlePerformanceMonitor.startMonitoring(operation);
export const endPaddleMonitoring = (operation: string) => paddlePerformanceMonitor.endMonitoring(operation);
export const endPaddleMonitoringWithError = (operation: string, error: string) => 
  paddlePerformanceMonitor.endMonitoringWithError(operation, error);

// 在开发环境中添加到全局对象
if (!import.meta.env.PROD && typeof window !== 'undefined') {
  (window as any).paddlePerformanceMonitor = paddlePerformanceMonitor;
}

export default paddlePerformanceMonitor;